/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from "@ngrx/signals";
import { PayerItem } from "../models/payer-item";
import { computed, inject } from "@angular/core";
import { UtcDateTimeFormatter } from "../helpers/utc-datetime-formatter";
import { MorDataService } from "../services/mor/mor-data.service";
import { FileDataTableRow } from "../components/sub-components/file-upload/file-upload-table/file-upload-table.component";
import { take } from 'rxjs/operators';
import { ColDef } from "ag-grid-community";
import { ViewProcessedRecordsDialogComponent, ViewProcessedRecordsDialogModel, ViewProcessedRecordsGridConfig } from "../dialogs/mor-data-view-details-dialog/view-processed-records-dialog/view-processed-records-dialog.component";
import { CmsFileDataRequest, MorFileProcessedDataRequest } from "../models/mor-file-processed-data-request";
import { ProcessedMorRecordsResult } from "../models/processed-mor-records-result";
import { FileDataTableValuesRequest } from "../models/file-data-table-values-request";
import { FileDataService } from "../services/file-upload/file-data.service";
import { FileDataTableItem } from "../models/file-data-table-item";
import { FileTypeFilterStore } from "./file-type-filter.store";
import { AsmFilesGridRow } from "../components/sub-components/asm-files/asm-files-grid/asm-files-grid.component";
import { AsmFilesGridResponse } from "../models/asm-files-grid-response";
import { FileDownloadRequest } from "../models/file-download-request";
import { GridResponse, MaoFileDataItem } from "../models/grid-data";
import { CmsFileService } from "../services/cms/cms-file.service";
import { MatDialog } from "@angular/material/dialog";
import { formatDate } from "@angular/common";
import { MyExportsGridItem } from "../models/my-exports-grid-item";
import { MyExportsGridResponse } from "../models/my-exports-grid-response";
import { ExportType } from "../models/export-type";
import { IQueryFilterRow } from "../helpers/ag-grid-export-helper";

type FileDataState = {
  selectedPayers: PayerItem[];
  asmSelectedPayers: PayerItem[];
  localThresholdDate: Date | null;
  asmThresholdDate: Date | null;
  fileTypeIds: number[];
  tableData: FileDataTableRow[];
  asmTableData: AsmFilesGridRow[];
  asmTableCount: number | null;
  asmGridSessionId: string | null;
  viewRecordsColumns: ColDef[] | null;
  viewRecordsData: object[] | null;
  morRecordType: string;
  myExportsData: MyExportsGridItem[];
  myExportsTableCount: number | null;
  myExportsGridSessionId: string | null;
  myExportsThresholdDate: Date | null;
  myExportsSelectedExportTypes: ExportType[];
};

type FileDataMode = 'asm' | 'myExports' | null;

const initialState: FileDataState = {
  selectedPayers: [],
  asmSelectedPayers: [],
  localThresholdDate: null,
  asmThresholdDate: null,
  fileTypeIds: [],
  tableData: [],
  asmTableData: [],
  asmTableCount: null,
  asmGridSessionId: null,
  viewRecordsColumns: null,
  viewRecordsData: [],
  morRecordType: '',
  myExportsData: [],
  myExportsTableCount: null,
  myExportsGridSessionId: null,
  myExportsThresholdDate: null,
  myExportsSelectedExportTypes: [],
};

const setPayersHelper = (store: any, payers?: PayerItem[], asm: boolean = false): void => {
  if (payers) {
    if (asm) {
      patchState(store, { asmSelectedPayers: payers });
      return;
    }
    patchState(store, { selectedPayers: payers });
  }
};

const dbFileItemsToTableDataHelper = (results: FileDataTableItem[]) => {
  const rows: FileDataTableRow[] = [];
  for (const item of results) {
    const row: FileDataTableRow = {
      id: item.ID,
      uploadDate: item.UploadDate,
      payerAlias: item.PayerAlias,
      uploadedBy: item.FirstName.charAt(0) + item.LastName,
      fileName: item.FileName,
      status: item.Status || 'Processing',
      fileType: item.FileType,
      minDos: item.MinDOS,
      maxDos: item.MaxDOS
    };
    rows.push(row);
  }
  return rows;
}

const getSelectedPayerIdsString = (state: FileDataState, mode: FileDataMode = null) => {
    let payerIds: string = "";
    const selectedPayers = mode === 'asm' ? state.asmSelectedPayers : state.selectedPayers;
    for (const payer of selectedPayers) {
      if (payerIds == "") {
          payerIds += `${payer.ID}`;
      } else {
          payerIds += `,${payer.ID}`;
      }
    }
    return payerIds;
}

const generateColumnDefs = (records: object[]): ColDef[] => {
  if (records.length === 0) {
    return [];
  }

  return Object.keys(records[0]).map((key) => ({
    field: key,
    headerName: key.charAt(0).toUpperCase() + key.slice(1),
    sortable: false
  } as ColDef)).filter(obj => obj.field != 'id');
}

export const FileDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((state) => ({
    formValid: computed(() => {
      return state.selectedPayers().length > 0 && state.fileTypeIds()?.length > 0;
    }),
    asmFormValid: computed(() => {
      return state.asmSelectedPayers().length > 0;
    }),
  })),
  withMethods(
    (
      store,
      utcHelper = inject(UtcDateTimeFormatter),
      fileService = inject(FileDataService),
      morService = inject(MorDataService),
      filterStore = inject(FileTypeFilterStore),
      cmsFileService = inject(CmsFileService),
      dialog = inject(MatDialog)
    ) => ({
      async setSelectedPayers(payers: PayerItem[], mode: FileDataMode = null) {
        setPayersHelper(store, payers, mode === 'asm');
        const ids = payers.map((payer) => {
          return payer.ID;
        });
        await filterStore.getMapFileTypesByPayerId(ids);
      },
      setThresholdDate(date: Date | null, mode: FileDataMode = null) {
        switch(mode) {
          case 'asm':
            patchState(store, { asmThresholdDate: date });
            return;
          case 'myExports':
            patchState(store, { myExportsThresholdDate: date });
            return;
          default:
            patchState(store, { localThresholdDate: date });
        }
      },
      setExportTypes(types: ExportType[] | null) {
        patchState(store, { myExportsSelectedExportTypes: types ?? [] });
      },
      setFileTypeIds(ids: number[] | null) {
        patchState(store, { fileTypeIds: ids ?? [] });
      },
      getTableData() {
        const state = getState(store);
        const query: FileDataTableValuesRequest = {
          payerIds: getSelectedPayerIdsString(state),
          thresholdDateString: state.localThresholdDate
            ? utcHelper.getFormattedUTCString(state.localThresholdDate)
            : "",
          fileTypeIds: state.fileTypeIds ?? [],
        };
        fileService
          .getFileDataTableItems(query)
          .subscribe((results?: FileDataTableItem[]) => {
            if (results) {
              const rows = dbFileItemsToTableDataHelper(results);
              patchState(store, { tableData: rows });
            }
          });
      },
      getProcessedRecordData(request: MorFileProcessedDataRequest): Promise<ViewProcessedRecordsGridConfig | null> {
        return new Promise((resolve, reject) => {
          if (request) {
            morService.getProcessedRecordDataByMorQueueID(request).pipe(take(1)).subscribe({
              next: (result: ProcessedMorRecordsResult) => {
                const columnDefs = generateColumnDefs(result.RecordData);
                patchState(store, { morRecordType: result.RecordType });
                resolve({
                  columnDefs: columnDefs,
                  rowData: result.RecordData,
                  listSize: result.ListSize,
                } as ViewProcessedRecordsGridConfig);
              },
              error: (err) => reject(err),
            });
          } else {
            resolve(null);
          }
        });
      },
      // Currently, only used for MAO files until MOR can be migrated to new workflow.
      getCmsFileDetails(request: CmsFileDataRequest): Promise<GridResponse<MaoFileDataItem> | null> {
        return new Promise((resolve, reject) => {
          if (request) {
            cmsFileService.getCmsFileData(request).pipe(take(1)).subscribe({
              next: (result: GridResponse<MaoFileDataItem>) => resolve(result),
              error: (err) => reject(err),
            });
          } else {
            resolve(null);
          }
        });
      },
      getAsmTableData() {
        const state = getState(store);
        const payerIds = getSelectedPayerIdsString(state, 'asm' as FileDataMode);
        const thresholdDateString = state.asmThresholdDate
            ? utcHelper.getFormattedUTCString(state.asmThresholdDate)
            : "";
        fileService
          .getAsmFilesGridData(payerIds, thresholdDateString)
          .subscribe((result?: AsmFilesGridResponse) => {
            if (result) {
              const rows = result.Items.map(item => {
                return {
                  rowToken: item.RowToken,
                  createDate: new Date(item.CreateDate + 'Z'),
                  payerAlias: item.PayerAlias,
                  createdBy: item.FirstName.charAt(0) + item.LastName,
                  recordCount: item.RecordCount,
                  templateName: item.TemplateName,
                  statusUpdated: item.StatusUpdated
                } as AsmFilesGridRow;
              });
              patchState(store, { asmTableData: rows, asmGridSessionId: result.GridSessionID, asmTableCount: result.ListSize });
            }
          });
      },
      downloadFile(rowToken: string, mode: string) {
        let sessionId: string | null = null;
        switch(mode) {
          case 'asm':
            sessionId = getState(store).asmGridSessionId;
            break;
          case 'myExports':
            sessionId = getState(store).myExportsGridSessionId;
            break;
          default:
            // handle error?
            return;
        }
        if (sessionId == null) {
          // show toast? reload grid?
          return;
        }
        const request: FileDownloadRequest = {
          GridSessionID: sessionId,
          RowToken: rowToken
        };
        return fileService
          .downloadFile(request)
          .pipe(take(1))
          .subscribe({
            next: () => {
              // download handled in service
            },
            error: () => {
              // interceptor handles errors
            }
          });
      },
      onOpenDialog(row: FileDataTableRow) {
        const model = {
          uploadDate: formatDate(row.uploadDate, 'MM/dd/yyyy', 'en-US'),
          uploadedBy: row.uploadedBy,
          fileType: row.fileType,
          payerAlias: row.payerAlias,
          id: row.id,
          listSize: 0
        }
        const dialogRef = dialog.open(ViewProcessedRecordsDialogComponent, new ViewProcessedRecordsDialogModel(model));
        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
          patchState(store, { morRecordType: '' });
        });
      },
      getMyExports() {
        const state = getState(store);
        const thresholdDateString = state.myExportsThresholdDate
            ? utcHelper.getFormattedUTCString(state.myExportsThresholdDate)
            : "";
        const exportTypeIds = state.myExportsSelectedExportTypes.map(et => et.id);
        fileService.getMyExports(thresholdDateString, exportTypeIds).pipe(take(1)).subscribe((response: MyExportsGridResponse) => {
          if (response) {
            const rows = response.Items.map((item) => {
              return {
                rowToken: item.RowToken,
                createDateTime: new Date(item.CreateDateTime + 'Z'),
                exportType: item.ExportType,
                fileName: item.FileName,
              } as MyExportsGridItem;
            });
            patchState(store, { 
              myExportsData: rows,
              myExportsTableCount: response.ListSize, 
              myExportsGridSessionId: response.GridSessionID 
            });
          }
        });
      },
      getCmsGridQueryFilters(): IQueryFilterRow[] {
        const state = getState(store);
        const filters: IQueryFilterRow[] = [];
        const payerValue = state.selectedPayers?.map((p?: PayerItem) => { return p?.Abbreviation; })?.join(', ') ?? 'None';
        const fileTypeValue = state.fileTypeIds?.map(
          (id?: number) => { 
            if (id) { 
              return filterStore.fileTypeOptions().find(i => i.value === id)?.label;
            }
          }
        )?.join(', ') ?? 'None';
        filters.push({
          label: 'Payer',
          value: payerValue,
          type: 'String'
        });
        filters.push({
          label: 'Min Upload Date',
          value: state.localThresholdDate?.toLocaleString() ?? '',
          type: 'String'
        });
        filters.push({
          label: 'File Type',
          value: fileTypeValue,
          type: 'String'
        });
        return filters;
      }
    })
  )
);